/**
 * Dominion product page: gallery slider, variant picker, AJAX add to bag, VIP release unlock.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {{ id: number, options: string[], available: boolean, price: string, compare: string | null, mediaId: number | null }} Variant
 */

if (!customElements.get('dg-product')) {
  class DgProduct extends HTMLElement {
    /** @type {Variant[]} */
    variants = [];
    /** @type {HTMLFormElement | null} */
    form = null;
    /** @type {HTMLElement | null} */
    track = null;
    /** @type {HTMLElement[]} */
    slides = [];

    connectedCallback() {
      const json = this.querySelector('[data-dg-product-json]');
      try {
        this.variants = JSON.parse(json?.textContent || '{}').variants || [];
      } catch {
        this.variants = [];
      }

      this.form = this.querySelector('form[action*="/cart/add"]');
      this.track = this.querySelector('[data-dg-track]');
      this.slides = [.../** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-dg-slide]'))];

      this.querySelectorAll('.dg-opt__input').forEach((input) => input.addEventListener('change', this.onOptionChange));
      this.querySelectorAll('[data-dg-qty]').forEach((btn) => btn.addEventListener('click', this.onQty));
      this.form?.addEventListener('submit', this.onSubmit);

      this.querySelector('[data-dg-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-dg-next]')?.addEventListener('click', () => this.step(1));
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.track?.addEventListener('keydown', this.onTrackKey);
      this.updateArrows();

      this.watchRelease();
    }

    /* ---------- Gallery ---------- */

    get index() {
      const track = this.track;
      if (!track || track.clientWidth === 0) return 0;
      return Math.round(track.scrollLeft / track.clientWidth);
    }

    /** @param {number} index */
    goTo(index) {
      const track = this.track;
      if (!track) return;
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      track.scrollTo({ left: clamped * track.clientWidth });
    }

    /** @param {number} delta */
    step(delta) {
      this.goTo(this.index + delta);
    }

    onScroll = () => {
      window.requestAnimationFrame(() => this.updateArrows());
    };

    /** @param {KeyboardEvent} event */
    onTrackKey = (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    };

    updateArrows() {
      const index = this.index;
      const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-dg-prev]'));
      const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-dg-next]'));
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= this.slides.length - 1;
      this.querySelectorAll('.dg-product__pip').forEach((pip, i) => pip.classList.toggle('is-active', i === index));
    }

    /* ---------- Variants ---------- */

    onOptionChange = () => {
      const selected = [...this.querySelectorAll('[data-dg-option]')].map((fieldset) => {
        const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('.dg-opt__input:checked'));
        const label = fieldset.querySelector('[data-dg-option-value]');
        if (label && checked) label.textContent = checked.value;
        return checked?.value ?? '';
      });

      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
      this.renderVariant(variant);
    };

    /** @param {Variant | undefined} variant */
    renderVariant(variant) {
      const idInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-dg-variant-id]'));
      const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-dg-add]'));
      const text = this.querySelector('[data-dg-add-text]');
      const price = this.querySelector('[data-dg-price]');

      if (idInput) idInput.value = variant ? String(variant.id) : '';

      if (button && text) {
        button.disabled = !variant || !variant.available;
        const key = !variant ? 'labelUnavailable' : variant.available ? 'labelAdd' : 'labelSoldout';
        text.textContent = button.dataset[key] || '';
      }

      if (price && variant) {
        price.textContent = variant.price;
        if (variant.compare) {
          const s = document.createElement('s');
          s.textContent = variant.compare;
          price.append(s);
        }
      }

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());

        if (variant.mediaId) {
          const slideIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === String(variant.mediaId));
          if (slideIndex >= 0) this.goTo(slideIndex);
        }
      }
    }

    /** @param {Event} event */
    onQty = (event) => {
      const button = /** @type {HTMLElement} */ (event.currentTarget);
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
      if (!input) return;
      const next = (parseInt(input.value, 10) || 1) + Number(button.dataset.dgQty);
      input.value = String(Math.max(1, next));
    };

    /* ---------- Add to bag ---------- */

    /** @param {SubmitEvent} event */
    onSubmit = async (event) => {
      event.preventDefault();
      const form = this.form;
      const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-dg-add]'));
      const text = this.querySelector('[data-dg-add-text]');
      const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-dg-error]'));
      if (!form || !button || button.disabled) return;

      const formData = new FormData(form);
      if (!formData.get('id')) return;

      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
        .filter(Boolean);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const quantity = Number(formData.get('quantity')) || 1;
      const deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(formData.get('id')), quantity }],
          promise: deferred.promise,
        })
      );

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add to bag');
        }

        const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'dg-product',
            itemCount: quantity,
            sections: data.sections,
            didError: false,
          },
        });

        if (text) text.textContent = button.dataset.labelAdded || '';
        window.setTimeout(() => {
          if (text) text.textContent = button.dataset.labelAdd || '';
        }, 1800);

        this.openCartDrawer();
      } catch (err) {
        deferred.reject(err);
        if (error) {
          error.textContent = err instanceof Error ? err.message : String(err);
          error.hidden = false;
        }
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };

    openCartDrawer() {
      const drawer = /** @type {(HTMLElement & { isOpen?: boolean, open?: () => void }) | null} */ (
        document.querySelector('#cart-drawer')
      );
      if (!drawer) {
        window.location.href = Theme.routes.cart_url;
        return;
      }
      // Horizon's drawer may auto-open itself; open it only if it didn't.
      window.setTimeout(() => {
        if (!drawer.isOpen) drawer.open?.();
      }, 150);
    }

    /* ---------- VIP release ---------- */

    watchRelease() {
      const release = this.dataset.release;
      if (!release) return;
      const time = Date.parse(release.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
      if (Number.isNaN(time)) return;

      const unlock = () => {
        const buy = /** @type {HTMLElement | null} */ (this.querySelector('[data-dg-buy]'));
        const pre = /** @type {HTMLElement | null} */ (this.querySelector('[data-dg-prerelease]'));
        if (buy) buy.hidden = false;
        if (pre) pre.hidden = true;
      };

      const remaining = time - Date.now();
      if (remaining <= 0) {
        unlock();
      } else if (remaining < 2 ** 31 - 1) {
        window.setTimeout(unlock, remaining);
      }
      document.addEventListener('dg:release-open', unlock);
    }
  }

  customElements.define('dg-product', DgProduct);
}
